import { LayoutComponent, Page } from "../interfaces/LayoutComponent";
import { Visit } from "../interfaces/Visit";

export const updateGridItem = (
  gridItem: LayoutComponent,
  comp: LayoutComponent
) => ({
  ...gridItem,
  minH: comp.minH,
  maxH: comp.maxH,
  minW: comp.minW,
  maxW: comp.maxW,
  h: comp.h,
  w: comp.w,
  data: {
    ...comp.data,
  },
});

export const updateLayoutItem = (layoutItem: any, comp: LayoutComponent) => ({
  ...layoutItem,
  minH: comp.minH,
  maxH: comp.maxH,
  minW: comp.minW,
  maxW: comp.maxW,
  h: comp.h,
  w: comp.w,
});

export const createNewLayoutComponent = (
  id: number,
  comp: LayoutComponent
) => ({
  i: id.toString(),
  x: comp.x,
  y: comp.y,
  h: comp.h,
  w: comp.w,
  minH: comp.minH,
  maxH: comp.maxH,
  minW: comp.minW,
  maxW: comp.maxW,
});

export const isComponentExist = (page: Page, componentId: number) => {
  return page.layout?.some(
    (layoutItem) => parseInt(layoutItem.i) === componentId
  );
};

export const removeComponent = (
  pages: Page[],
  pageIndex: number,
  componentId: number
): Page[] => {
  // Remove component from gridItems
  const updatedGridItems = pages[pageIndex].gridItems.filter(
    (item) => item.id !== componentId
  );

  // Remove component from layout
  const updatedLayout = pages[pageIndex].layout.filter(
    (item) => parseInt(item.i) !== componentId
  );

  // Update the page with new gridItems and layout
  const updatedPages = [...pages];
  updatedPages[pageIndex] = {
    ...updatedPages[pageIndex],
    gridItems: updatedGridItems,
    layout: updatedLayout,
  };

  return updatedPages;
};

export const getNewValue = (component: string, e: any): any => {
  switch (component) {
    case "AmsaStaticSelect":
    case "AmsaEditor":
    case "PhysiotherapistChart":
      return e;
    case "AmsaInput":
    case "AmsaDateInput":
    case "LMP":
      return e.target.value;
    case "GTPAL":
      // GTPAL sends JSON string, parse it to object
      try {
        return JSON.parse(e.target.value);
      } catch {
        return e.target.value;
      }
    default:
      return null;
  }
};

// Helper function to generate layout object
export const generateLayoutObject = (
  gridItems: LayoutComponent[]
): {
  layoutContent: Record<string, string>;
  vitals: Record<string, any>;
  defaultFields: Record<keyof Visit, string>;
} => {
  const layoutContent: Record<string, string> = {};
  const vitals: Array<Record<string, string>> = [];
  const defaultFields = {} as Record<keyof Visit, string>;

  gridItems.forEach((item) => {
    if (item.data.isDefault) {
      defaultFields[item.data.key as keyof Visit] = item.data.value;
    } else if (item.data.isVital) {
      vitals.push({
        label: item.data.key,
        value: item.data.value,
        unit: item.data.selectedUnit,
      });
    } else {
      // For GTPAL and other components that store objects, stringify them
      const valueToStore =
        item.component === "GTPAL" && typeof item.data.value === "object"
          ? JSON.stringify(item.data.value)
          : String(item.data.value || "");
      layoutContent[item.data.key] = valueToStore;
    }
  });

  return { layoutContent, vitals, defaultFields };
};

// Helper function to update the grid items
export const updateGridItems = (
  gridItems: LayoutComponent[],
  gridItemToUpdate: LayoutComponent,
  e: any
): LayoutComponent[] => {
  return gridItems.map((item) => {
    if (item.id !== gridItemToUpdate.id) return item;

    const newValue = getNewValue(gridItemToUpdate.component, e);

    // Only update the item if the value has changed
    if (item.data.value !== newValue) {
      return {
        ...item,
        data: { ...item.data, value: newValue },
      };
    }

    return item;
  });
};
